import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Power, PowerOff, User, Shield } from "lucide-react";
import { toast } from "sonner";

import AdminPageHeader from "@/components/layout/AdminPageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { SCREENS } from "@/constants/testIds";
import {
  ADMIN_ROLES, ADMIN_ROLE_LABELS, ADMIN_MODULES, ADMIN_PERMISSIONS,
  listarUsuarios, crearUsuario, editarUsuario, setUsuarioActivo, getAdminSession,
  hidratarUsuarios,
} from "@/data/adminAuthStore";

const ROLES = Object.values(ADMIN_ROLES);
const MODULOS_KEYS = Object.keys(ADMIN_MODULES);

const emptyForm = { usuario: "", password: "", rol: ADMIN_ROLES.EVENTOS, nombre: "", puesto: "", correo: "", telefono: "" };

export default function AdminUsuarios() {
  const [tick, setTick] = useState(0);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const usuarios = useMemo(() => listarUsuarios(), [tick]);
  const sesion = getAdminSession();

  useEffect(() => {
    hidratarUsuarios().then(() => setTick((t) => t + 1));
  }, []);

  useEffect(() => {
    if (dialog?.mode === "edit" && dialog.user) {
      setForm({
        usuario: dialog.user.usuario, password: "", rol: dialog.user.rol,
        nombre: dialog.user.nombre || "", puesto: dialog.user.puesto || "",
        correo: dialog.user.correo || "", telefono: dialog.user.telefono || "",
      });
    } else if (dialog?.mode === "new") {
      setForm(emptyForm);
    }
  }, [dialog]);

  const setField = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const guardar = async () => {
    if (!form.nombre.trim() || !form.usuario.trim() || !form.rol) {
      toast.error("Nombre, usuario y rol son obligatorios.");
      return;
    }
    setSaving(true);
    // Mensajes específicos para cada tipo de error del backend
    const mensajeError = (res, accion) => {
      const err = (res && res.error) || "";
      if (err === "duplicado")
        return "Ya existe un usuario con ese nombre. Elige otro.";
      if (err === "network")
        return "Sin conexión con el servidor. Intenta de nuevo.";
      if (typeof err === "string" && err.startsWith("Rol inválido"))
        return err;
      if (typeof err === "string" && err.trim()) return err;
      return `No se pudo ${accion} el usuario.`;
    };
    if (dialog.mode === "new") {
      if (!form.password) {
        toast.error("Contraseña obligatoria para usuario nuevo.");
        setSaving(false);
        return;
      }
      const res = await crearUsuario(form);
      setSaving(false);
      if (!res || res.error) {
        toast.error(mensajeError(res, "crear"));
        return;
      }
      toast.success(`Usuario "${res.usuario}" creado.`);
    } else {
      const cambios = { ...form };
      if (!form.password) delete cambios.password;
      delete cambios.usuario;
      const res = await editarUsuario({ usuario: dialog.user.usuario, cambios });
      setSaving(false);
      if (!res || res.error) {
        toast.error(mensajeError(res, "actualizar"));
        return;
      }
      toast.success("Usuario actualizado.");
    }
    setDialog(null);
    setTick((t) => t + 1);
  };

  const toggleActivo = async (u) => {
    if (u.usuario === sesion?.usuario) {
      toast.error("No puedes desactivarte a ti mismo.");
      return;
    }
    const res = await setUsuarioActivo(u.usuario, !u.activo);
    if (!res || res.error) {
      const err = res?.error || "";
      const msg =
        err === "network"
          ? "Sin conexión con el servidor."
          : typeof err === "string" && err.trim()
            ? err
            : "No se pudo cambiar el estado.";
      toast.error(msg);
      return;
    }
    toast.success(u.activo ? "Usuario desactivado." : "Usuario activado.");
    setTick((t) => t + 1);
  };

  return (
    <div data-testid={SCREENS.adminUsuarios} className="flex flex-col gap-8">
      <AdminPageHeader eyebrow="Portal administrativo" title="Usuarios internos" description="Catálogo de usuarios operativos del portal." />

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{usuarios.length} usuario(s)</p>
        <Button data-testid="usuarios-btn-nuevo" onClick={() => setDialog({ mode: "new" })} className="rounded-xl bg-primary text-primary-foreground">
          <Plus className="w-4 h-4 mr-2" /> Nuevo usuario
        </Button>
      </div>

      <div className="rounded-2xl border hairline bg-card overflow-hidden">
        <Table>
          <TableHeader className="bg-muted/40">
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Puesto</TableHead>
              <TableHead>Usuario</TableHead>
              <TableHead>Correo</TableHead>
              <TableHead>Rol</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {usuarios.map((u) => (
              <TableRow key={u.usuario} data-testid={`usuarios-row-${u.usuario}`}>
                <TableCell className="font-medium">{u.nombre}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{u.puesto || "—"}</TableCell>
                <TableCell className="text-sm">{u.usuario}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{u.correo || "—"}</TableCell>
                <TableCell>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 text-accent border border-accent/30 px-2.5 py-0.5 text-[11px] font-medium">
                    {ADMIN_ROLE_LABELS[u.rol]}
                  </span>
                </TableCell>
                <TableCell>
                  <span data-testid={`usuarios-estado-${u.usuario}`} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${u.activo === false ? "bg-muted text-muted-foreground" : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"}`}>
                    {u.activo === false ? "Inactivo" : "Activo"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <Button size="sm" variant="ghost" data-testid={`usuarios-edit-${u.usuario}`} onClick={() => setDialog({ mode: "edit", user: u })}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`usuarios-toggle-${u.usuario}`} onClick={() => toggleActivo(u)}>
                      {u.activo === false ? <Power className="w-3.5 h-3.5 text-emerald-600" /> : <PowerOff className="w-3.5 h-3.5 text-muted-foreground" />}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Matriz de roles y permisos (arquitectura preparada, sólo consulta) */}
      <section className="rounded-2xl border hairline bg-card p-6 flex flex-col gap-4">
        <header className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent/10 text-accent ring-1 ring-accent/30 grid place-items-center">
            <Shield className="w-4 h-4" strokeWidth={1.7} />
          </div>
          <div>
            <h3 className="font-heading text-lg leading-tight">Roles y permisos</h3>
            <p className="text-xs text-muted-foreground">Matriz base por módulo. Editable en un sprint posterior.</p>
          </div>
        </header>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-muted/40">
              <TableRow>
                <TableHead>Módulo</TableHead>
                {ROLES.map((r) => <TableHead key={r} className="text-center">{ADMIN_ROLE_LABELS[r]}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {MODULOS_KEYS.map((mod) => (
                <TableRow key={mod} data-testid={`permisos-row-${mod}`}>
                  <TableCell className="capitalize">{mod}</TableCell>
                  {ROLES.map((r) => (
                    <TableCell key={r} className="text-center">
                      {ADMIN_PERMISSIONS[r]?.[mod] ? (
                        <span className="text-emerald-600">✓</span>
                      ) : (
                        <span className="text-muted-foreground/60">—</span>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Cada permiso soporta: Ver · Crear · Editar · Publicar · Cancelar · Cerrar · Configurar.
          En este sprint todos los roles activos acceden a todos los módulos disponibles.
        </p>
      </section>

      <Dialog open={!!dialog} onOpenChange={(o) => (!o ? setDialog(null) : null)}>
        <DialogContent data-testid="usuarios-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><User className="w-4 h-4" /> {dialog?.mode === "new" ? "Nuevo usuario" : "Editar usuario"}</DialogTitle>
            <DialogDescription>Los usuarios internos operan el portal. No se eliminan; sólo se desactivan.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label htmlFor="usuarios-form-nombre">Nombre *</Label><Input id="usuarios-form-nombre" data-testid="usuarios-form-nombre" value={form.nombre} onChange={setField("nombre")} /></div>
            <div><Label htmlFor="usuarios-form-puesto">Puesto</Label><Input id="usuarios-form-puesto" data-testid="usuarios-form-puesto" value={form.puesto} onChange={setField("puesto")} /></div>
            <div><Label htmlFor="usuarios-form-usuario">Usuario *</Label><Input id="usuarios-form-usuario" data-testid="usuarios-form-usuario" value={form.usuario} onChange={setField("usuario")} disabled={dialog?.mode === "edit"} /></div>
            <div><Label htmlFor="usuarios-form-password">{dialog?.mode === "new" ? "Contraseña *" : "Nueva contraseña"}</Label><Input id="usuarios-form-password" type="password" data-testid="usuarios-form-password" value={form.password} onChange={setField("password")} placeholder={dialog?.mode === "edit" ? "Dejar vacío para mantener" : ""} /></div>
            <div><Label htmlFor="usuarios-form-correo">Correo</Label><Input id="usuarios-form-correo" type="email" data-testid="usuarios-form-correo" value={form.correo} onChange={setField("correo")} /></div>
            <div><Label htmlFor="usuarios-form-telefono">Teléfono</Label><Input id="usuarios-form-telefono" data-testid="usuarios-form-telefono" value={form.telefono} onChange={setField("telefono")} /></div>
            <div className="col-span-2">
              <Label>Rol *</Label>
              <Select value={form.rol} onValueChange={(v) => setForm((s) => ({ ...s, rol: v }))}>
                <SelectTrigger data-testid="usuarios-form-rol"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ADMIN_ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancelar</Button>
            <Button data-testid="usuarios-form-guardar" onClick={guardar} disabled={saving}>{saving ? "Guardando…" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
